<template>
  <div>
    <!-- Banner: Only show tests with status "Collected" (ST-011) -->
    <ol
      class="flex items-center w-full p-3 justify-around space-x-2 text-sm font-medium text-center text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm sm:text-base"
    >
      <li
        v-for="(request, index) in filteredRequests"
        :key="request.id"
        class="flex items-center cursor-pointer"
        :class="{ 'text-custom-blue': selectedTest === request.id }"
        @click="selectTest(request.id)"
      >
        <span
          class="flex items-center justify-center w-5 h-5 me-2 text-xs border rounded-full shrink-0"
          :class="selectedTest === request.id ? 'border-custom-blue' : 'border-gray-500'"
        >
          {{ index + 1 }}
        </span>
        {{ request.test_type.name }}
      </li>
    </ol>

    <!-- No pending tests message -->
    <div v-if="filteredRequests.length === 0" class="text-center text-green-600 font-medium mt-6">
      All lab tests have been processed!
    </div>

    <!-- Main Form (active for ST-011 tests OR when adding an amendment) -->
    <div v-if="selectedTest && (currentRequest.status?.code === 'ST-011' || amendingTest)" class="grid grid-cols-1 gap-4 h-[55vh] overflow-y-auto mt-3">
      <!-- Amendment mode banner -->
      <div v-if="amendingTest" class="mx-8 mt-3 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800 flex items-center justify-between">
        <span>Adding amendment for <strong>{{ currentRequest.test_type?.name }}</strong> — previous results are preserved.</span>
        <button @click="cancelAmendment" class="text-xs text-yellow-700 underline hover:text-yellow-900">Cancel</button>
      </div>
      <div class="grid grid-cols-2 gap-6 mt-4 px-8">
        <div class="mb-4">
          <h3 class="font-medium">Test Type:</h3>
          <p class="text-sm">{{ currentRequest.test_type?.name }}</p>
        </div>
        <div class="mb-4">
          <h3 class="font-medium">Sample ID:</h3>
          <p class="text-sm">{{ currentRequest.sample_code }}</p>
        </div>
        <div class="mb-4">
          <h3 class="font-medium">Date &amp; Time Sample Collected:</h3>
          <p class="text-sm">{{ formattedTimestamp(currentRequest.collected_at) }}</p>
        </div>
        <!-- Sample collector — carried forward from the collection stage -->
        <div class="mb-4">
          <h3 class="font-medium">Sample Collected By:</h3>
          <p class="text-sm font-semibold text-blue-700">
            {{ currentRequest.collecter?.Username || '—' }}
          </p>
        </div>

        <!-- Analyte Entry Table (when analytes are linked) -->
        <div v-if="currentAnalytes.length > 0" class="col-span-2">
          <label class="font-medium block mb-2">Test Results:</label>
          <div v-if="analytesLoading" class="text-sm text-gray-400 italic">Loading analytes...</div>
          <table v-else class="w-full text-sm border border-gray-200 rounded">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-2 text-left font-medium text-gray-600">Analyte</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600">Result</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600">Unit</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600">Reference Range</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600">Flag</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="analyte in currentAnalytes" :key="analyte.id">
                <td class="px-3 py-2 font-medium">
                  {{ analyte.name }}
                  <span v-if="analyte.abbreviation" class="text-xs text-gray-400 ml-1">({{ analyte.abbreviation }})</span>
                </td>
                <td class="px-3 py-2">
                  <!-- Numeric -->
                  <input
                    v-if="analyte.result_type === 'numeric'"
                    type="number"
                    step="any"
                    v-model="labForms[selectedTest].analyte_results[analyte.id]"
                    class="w-28 border border-gray-300 rounded px-2 py-1 bg-gray-50 focus:ring-1 focus:ring-blue-400"
                    placeholder="0.0"
                  />
                  <!-- Select -->
                  <select
                    v-else-if="analyte.result_type === 'select'"
                    v-model="labForms[selectedTest].analyte_results[analyte.id]"
                    class="w-40 border border-gray-300 rounded px-2 py-1 bg-gray-50"
                  >
                    <option value="" disabled>Select...</option>
                    <option v-for="opt in analyte.select_options" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                  <!-- Text / calculated -->
                  <input
                    v-else
                    type="text"
                    v-model="labForms[selectedTest].analyte_results[analyte.id]"
                    class="w-40 border border-gray-300 rounded px-2 py-1 bg-gray-50"
                    placeholder="Enter value"
                  />
                </td>
                <td class="px-3 py-2 text-gray-500">{{ analyte.unit || '—' }}</td>
                <td class="px-3 py-2 text-gray-500 text-xs">
                  <span v-if="analyte.has_reference_range && analyte.ref_range_adult_male_low != null">
                    {{ analyte.ref_range_adult_male_low }} – {{ analyte.ref_range_adult_male_high }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td class="px-3 py-2">
                  <span
                    v-if="liveFlag(analyte, labForms[selectedTest].analyte_results[analyte.id]) === 'H'"
                    class="px-2 py-0.5 text-xs font-bold rounded bg-red-100 text-red-700"
                  >H</span>
                  <span
                    v-else-if="liveFlag(analyte, labForms[selectedTest].analyte_results[analyte.id]) === 'L'"
                    class="px-2 py-0.5 text-xs font-bold rounded bg-blue-100 text-blue-700"
                  >L</span>
                  <span
                    v-else-if="liveFlag(analyte, labForms[selectedTest].analyte_results[analyte.id]) === 'N'"
                    class="px-2 py-0.5 text-xs font-bold rounded bg-green-100 text-green-700"
                  >N</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Fallback text input (services with no linked analytes) -->
        <div v-else class="flex flex-col">
          <label class="font-medium">Test Results:</label>
          <input
            type="text"
            v-model="labForms[selectedTest].results"
            placeholder="Enter results..."
            class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"
            required
          />
        </div>


        <!-- Lab Comments -->
        <div class="flex flex-col">
          <label class="font-medium">Lab Comments:</label>
          <textarea
            v-model="labForms[selectedTest].comment"
            rows="4"
            placeholder="Add comments..."
            class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"
          />
        </div>

        <!-- File Upload -->
        <div class="flex flex-col">
          <label class="font-medium">
            Upload Lab Results (Max 5 files, 10MB each)<br />
            <span class="text-xs text-gray-600">JPG, PNG, PDF</span>
          </label>
          <label
            for="fileInput"
            class="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-500 rounded bg-gray-50 mt-2 cursor-pointer hover:bg-gray-100 transition"
            @click="logButtonClick"
          >
            <input
              id="fileInput"
              type="file"
              ref="fileInput"
              class="hidden"
              @change="handleFileSelect"
              accept=".jpg,.jpeg,.png,.pdf"
              multiple
            />
            <img v-if="selectedFiles.length === 0" :src="uploadFile" alt="upload" class="w-6 h-6" />
            <span v-if="selectedFiles.length === 0" class="text-sm text-blue-600">Add Lab Reports</span>
            <span v-else class="text-sm text-blue-600">{{ selectedFiles.length }} file(s) selected</span>
          </label>

          <!-- File Preview -->
          <div v-if="selectedFiles.length > 0" class="flex flex-wrap gap-3 mt-3">
            <div v-for="(file, index) in selectedFiles" :key="index" class="flex flex-col items-center gap-1 p-2 bg-gray-50 rounded max-w-[100px]">
              <span class="text-xs text-gray-600 text-center truncate w-full" :title="file.file.name">{{ file.file.name }}</span>

              <div v-if="file.file.type.startsWith('image/')" class="w-20 h-20">
                <img :src="file.preview" alt="preview" class="w-full h-full object-cover rounded" />
              </div>
              <div v-else-if="file.file.type === 'application/pdf'" class="w-20 h-20 bg-red-50 border-2 border-red-200 rounded flex items-center justify-center">
                <span class="text-xs font-bold text-red-600">PDF</span>
              </div>

              <div v-if="uploadProgress[index]" class="w-full">
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-custom-yellow h-2 rounded-full transition-all" :style="{ width: uploadProgress[index] + '%' }"></div>
                </div>
              </div>

              <button @click="removeFile(index)" class="text-red-500 hover:text-red-700">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="mt-4 px-8 flex justify-end items-center">
        <button
          :disabled="showLoader || uploadInProgress"
          @click="handleTestSubmit"
          class="px-6 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600 disabled:opacity-50"
        >
          {{ showLoader ? 'Submitting...' : (amendingTest ? 'Submit Amendment' : 'Submit Test') }}
        </button>
      </div>
    </div>

    <!-- Submitted Results Table -->
    <div class="mt-8 px-8">
      <h2 class="text-xl font-bold mb-4">Submitted Test Results</h2>
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Test</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Version</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Results</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Reports</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Collected By</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Conducted By</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Status</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600">Comments</th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="(test, idx) in submittedTest" :key="`${test.labRequestId}-${idx}`">
            <td class="px-4 py-4 whitespace-nowrap">{{ test.test_type }}</td>
            <td class="px-4 py-4 whitespace-nowrap">
              <span
                class="px-2 py-0.5 text-xs font-semibold rounded-full"
                :class="test.versionLabel === 'Latest' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'"
              >
                {{ test.versionLabel }}
              </span>
              <p v-if="test.created_at" class="text-xs text-gray-400 mt-0.5">
                {{ new Date(test.created_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) }}
              </p>
            </td>
            <td class="px-4 py-4">
              <div v-if="parseAnalyteBlob(test.result)" class="space-y-0.5">
                <div v-for="(entry, name) in parseAnalyteBlob(test.result)" :key="name" class="text-xs whitespace-nowrap">
                  <span class="font-medium text-gray-700">{{ name }}:</span>
                  <span :class="entry.flag === 'H' || entry.flag === 'L' ? 'font-semibold text-red-700' : 'text-gray-800'">{{ entry.value }}</span>
                  <span v-if="entry.unit" class="text-gray-400">{{ entry.unit }}</span>
                  <span v-if="entry.flag === 'H' || entry.flag === 'L'" class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700">{{ entry.flag }}</span>
                </div>
              </div>
              <span v-else class="whitespace-nowrap">{{ test.result }}</span>
            </td>
            <td class="px-4 py-4">
              <div v-for="(file, i) in test.url" :key="i" class="mb-1">
                <span class="text-blue-600 cursor-pointer hover:underline text-sm" @click="downloadFile(file.url)">
                  {{ file.filename || 'Download File' }}
                </span>
              </div>
              <span v-if="!test.url || test.url.length === 0" class="text-gray-500 text-sm">No Attachment</span>
            </td>
            <td class="px-4 py-4 whitespace-nowrap text-sm">{{ test.collected_by || '—' }}</td>
            <td class="px-4 py-4 whitespace-nowrap text-sm">{{ test.filled_by || '—' }}</td>
            <td class="px-4 py-4 whitespace-nowrap">
              <span
                class="px-3 py-1 text-xs font-semibold rounded-full"
                :class="{
                  'bg-green-100 text-green-800': test.status === 'Published',
                  'bg-yellow-100 text-yellow-800': test.status === 'Completed',
                  'bg-gray-100 text-gray-800': !test.status
                }"
              >
                {{ test.status || 'Draft' }}
              </span>
            </td>
            <td class="px-4 py-4 whitespace-nowrap">{{ test.comment }}</td>
            <td class="px-4 py-4 whitespace-nowrap">
              <button
                v-if="test.versionLabel === 'Latest'"
                @click="amendTest(test.labRequestId)"
                class="text-xs px-3 py-1 rounded border border-custom-blue text-custom-blue hover:bg-blue-50"
              >
                Add Amendment
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import uploadFile from "@/assets/icons/upload.png";
import apiClient from "@/services/api";
import AWS from "aws-sdk";
import axios from "axios";

export default {
  name: "ConductTestTab",
  props: {
    requests: { type: Array, required: true },
  },
  data() {
    return {
      selectedTest: null,
      amendingTest: false,
      labForms: {},
      analytes: {},
      analytesLoading: false,
      selectedFiles: [],
      uploadProgress: [],
      showLoader: false,
      uploadFile,
    };
  },
  computed: {
    filteredRequests() {
      const filtered = this.requests.filter(r => r.status?.code === 'ST-011');
      if (filtered.length && !this.selectedTest) {
        this.$nextTick(() => this.selectTest(filtered[0].id));
      }
      return filtered;
    },
    currentRequest() {
      return this.requests.find(r => r.id === this.selectedTest) || {};
    },
    currentAnalytes() {
      return this.analytes[this.selectedTest] || []
    },
    uploadInProgress() {
      return this.uploadProgress.some(p => p > 0 && p < 100);
    },
    submittedTest() {
      const rows = [];
      this.requests
        .filter(r => r.lab_results?.length > 0)
        .forEach(r => {
          const versions = [...r.lab_results].reverse(); // newest first
          versions.forEach((result, idx) => {
            rows.push({
              labRequestId: r.id,
              test_type:    r.test_type.name,
              versionLabel: idx === 0 ? 'Latest' : `Amendment ${versions.length - 1 - idx}`,
              result:       result.results,
              url:          result.url || [],
              collected_by: r.collecter?.Username || null,
              filled_by:    result.filled_by?.Username || null,
              comment:      result.comments || '',
              status:       r.status?.name || '',
              created_at:   result.created_at,
            });
          });
        });
      return rows;
    },
  },
  watch: {
    requests: {
      handler(newVal) {
        if (newVal?.length && !this.selectedTest) {
          const pending = newVal.filter(r => r.status?.code === 'ST-011');
          if (pending.length) this.selectTest(pending[0].id);
        }
      },
      immediate: true,
    },
  },

  methods: {
    // Structured lab capture stores results as a JSON blob (`{ "Analyte": { value, unit, flag } }`);
    // older free-text results aren't JSON, so this returns null and the raw string is shown instead.
    parseAnalyteBlob(resultString) {
      if (!resultString || typeof resultString !== 'string') return null;
      try {
        const parsed = JSON.parse(resultString);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length) {
          return parsed;
        }
        return null;
      } catch {
        return null;
      }
    },

    selectTest(id) {
      this.selectedTest  = id;
      this.amendingTest  = false;
      if (!this.labForms[id]) {
        this.labForms[id] = { analyte_results: {}, results: '', comment: '', url: [] };
      }

      this.loadAnalytes(id)
    },
    amendTest(id) {
      this.selectedTest  = id;
      this.amendingTest  = true;
      this.labForms[id]  = { analyte_results: {}, comment: '', url: [] };
      this.selectedFiles = [];
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
    },
    cancelAmendment() {
      this.amendingTest = false;
      this.selectedTest = null;
      this.selectedFiles = [];
    },
    liveFlag(analyte, value) {
      if (analyte.result_type !== 'numeric' || !analyte.has_reference_range) return null;
      const v = parseFloat(value);
      if (isNaN(v) || value === '') return null;
      const low  = parseFloat(analyte.ref_range_adult_male_low);
      const high = parseFloat(analyte.ref_range_adult_male_high);
      if (!isNaN(low) && v < low)  return 'L';
      if (!isNaN(high) && v > high) return 'H';
      return 'N';
    },

    formattedTimestamp(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });
    },
    logButtonClick() {
      console.log("Add Lab Reports clicked");
    },
    async loadAnalytes(testId) {
      const request = this.requests.find((r) => r.id === testId)
      if (!request?.test_type?.id) return;

      this.loadingAnalytes = true
      try {
        const res = await apiClient.fetchAnalytesForService(request.test_type.id)
        if (res.success && res.data) {
          this.analytes = {
            ...this.analytes,
            [testId]: res.data
          }
        } else {
          this.analytes = {
            ...this.analytes,
            [testId]: []
          }
        }

      } catch (e) {
          this.analytes = {
          ...this.analytes,
          [testId]: []
        }
        this.$toast.error('Error loading analytes')
        console.log('Error loading analytes', e)
      } finally {
        this.loadingAnalytes = false
      }
    },
    async handleTestSubmit() {
      const analyteResults = Object.entries(this.labForms[this.selectedTest]?.analyte_results || {})
        .filter(([, v]) => v !== '' && v !== null && v !== undefined)
        .map(([lab_analyte_id, value]) => ({ lab_analyte_id, value }));

      const hasAnalytes    = this.currentAnalytes.length > 0;
      const legacyResults  = this.labForms[this.selectedTest]?.results?.trim();

      if (hasAnalytes && analyteResults.length === 0) {
        this.$toast.error("Enter at least one analyte result.");
        return;
      }
      if (!hasAnalytes && !legacyResults) {
        this.$toast.error("Test results are required.");
        return;
      }

      this.showLoader = true;
      try {
        const uploadResults    = await this.uploadLabFiles();
        const successfulUploads = uploadResults.filter(r => r.success);
        if (successfulUploads.length < uploadResults.length && this.selectedFiles.length > 0) {
          this.$toast.warning("Some files failed to upload");
        }

        this.labForms[this.selectedTest].url = successfulUploads.map(r => ({
          url: r.url, filename: r.filename
        }));

        const payload = {
          labRequestId:    this.selectedTest,
          analyte_results: analyteResults,
          results:         legacyResults || '',  // kept for services with no analytes
          comment:         this.labForms[this.selectedTest].comment,
          url:             this.labForms[this.selectedTest].url,
          userId:          this.currentRequest.requested_by?.id,
          facilityId:      this.currentRequest.facility_id,
        };

        const response = await apiClient.captureSampleResult(payload);
        if (!response.success) {
          this.$toast.error("Failed to save results");
          return;
        }

        this.$toast.success(this.amendingTest ? "Amendment saved successfully" : "Results saved successfully");
        this.amendingTest = false;
        this.$emit("refetch-requests");

        await this.$nextTick();
        await new Promise(resolve => this.$nextTick(resolve));

        const nextPending = this.requests.find(r =>
          r.status?.code === 'ST-011' && r.id !== this.selectedTest
        );
        if (nextPending) {
          this.selectTest(nextPending.id);
        } else {
          this.selectedTest = null;
        }
        this.resetForm();
      } catch (err) {
        this.$toast.error("Network error");
        console.error(err);
      } finally {
        this.showLoader = false;
      }
    },

    async uploadLabFiles() {
      if (!this.selectedFiles.length) return [];
      this.uploadProgress = new Array(this.selectedFiles.length).fill(0);

      const results = await Promise.allSettled(
        this.selectedFiles.map(async (fileObj, index) => {
          const file = fileObj.file;
          const s3 = new AWS.S3({
            endpoint: process.env.VUE_APP_DO_ENDPOINT,
            signatureVersion: "v4",
            accessKeyId: process.env.VUE_APP_DO_ACCESS_KEY,
            secretAccessKey: process.env.VUE_APP_DO_SECRET_KEY,
            region: process.env.VUE_APP_DO_REGION,
          });
          const timestamp = Date.now();
          const key = `lab_result_${this.selectedTest}_${timestamp}_${file.name}`;
          const params = {
            Bucket: "jiji-bucket",
            Key: key,
            Body: file,
            ACL: "public-read",
            ContentType: file.type,
          };
          try {
            const upload = s3.upload(params);
            upload.on("httpUploadProgress", (progress) => {
              const percentage = Math.round((progress.loaded / progress.total) * 100);
              this.$set(this.uploadProgress, index, percentage);
            });
            const result = await upload.promise();
            return { success: true, url: result.Location, filename: file.name };
          } catch (error) {
            console.error(`Upload failed for ${file.name}:`, error);
            return { success: false, url: "", filename: file.name, error: error.message };
          }
        })
      );
      return results.map(r => (r.status === "fulfilled" ? r.value : r.reason));
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
    },
    addFiles(files) {
      const validFiles = files.filter(
        file =>
          ["image/jpeg", "image/png", "application/pdf"].includes(file.type) &&
          file.size <= 10 * 1024 * 1024
      );
      if (validFiles.length < files.length) {
        this.$toast.error("Some files were rejected: Only .jpg, .jpeg, .png, .pdf under 10MB allowed.");
      }
      const newFiles = validFiles.map(file => ({
        file,
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      }));
      this.selectedFiles = [...this.selectedFiles, ...newFiles].slice(0, 5);
      if (this.selectedFiles.length > 5) {
        this.$toast.error("Maximum 5 files allowed.");
        this.selectedFiles = this.selectedFiles.slice(0, 5);
      }
    },
    removeFile(index) {
      const file = this.selectedFiles[index];
      if (file.preview) URL.revokeObjectURL(file.preview);
      this.selectedFiles.splice(index, 1);
      this.uploadProgress.splice(index, 1);
    },

    resetForm() {
      this.selectedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
      this.selectedFiles = [];
      this.uploadProgress = [];
      if (this.selectedTest) {
        this.labForms[this.selectedTest] = { analyte_results: {}, comment: '', url: [] };
      }
    },

    async downloadFile(fileUrl) {
      try {
        const response = await axios.get(fileUrl, { responseType: "blob" });
        let fileName = fileUrl.split("/").pop().split("?")[0];
        const cd = response.headers["content-disposition"];
        if (cd) {
          const match = cd.match(/filename="?([^"]+)"?/);
          if (match) fileName = match[1];
        }
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Download failed:", error);
        this.$toast.error("Failed to download file");
      }
    },
  },
};
</script>

<style scoped>
label,
button {
  pointer-events: auto !important;
  cursor: pointer !important;
}
</style>